import { getEnv } from "../../../utils/env"
import firebase, { auth, storage, analytics } from "../firebase"
import { trackEvent } from "../../mp-utils/generic-utils/Events/eventService"
import { EventAction } from "../../mp-utils/generic-utils"
import * as _ from "lodash"
import { get, Doc, remove, collection } from "typesaurus"
import { AssetRef, AssetType, MediaType } from "../db/types"
import { Asset, Media, WordBank } from "../db/collections"
import { WordBankSchema } from "../db/schemas"
import { rewriteImageAlt } from "../helpers/utils/rewriteImageAlt"
import {
  createAssetRecord,
  createLibraryRecord,
  createMeta,
  getAssetLocation,
  addWordBank,
  updateAssetRecord,
  updateLibraryRecord,
  getBlockImageRecord,
  getBlockTextRecord,
  updateBlockImageRecord,
  updateBlockTextRecord,
  getLibraryRecord,
  getSnapRecord,
  getScreenRecord,
  updateScreenRecord,
  getQtiInteractionRecord,
  updateQtiInteractionRecord,
  getInteractionRecord,
  updateInteractionRecord,
  getAssetRecord,
  getBlockVideoRecord,
  updateBlockVideoRecord
} from "../db/helpers"
import axios, { AxiosRequestConfig } from "axios"

const config: AxiosRequestConfig = {
  baseURL: getEnv("AUTHORING_API"),
  // baseURL: "http://localhost:8080",
  responseType: "json"
}
const client = axios.create(config)

const CACHE_TTL_MS = 15 * 60 * 1000

export const fetchMuxAsset = async (playbackId: string, forceRefresh?: boolean) => {
  // Check Firestore cache first (unless forcing refresh)
  if (!forceRefresh) {
    const cached = await getAssetRecord(`mux_${playbackId}`)
    if (cached?.data?.muxData) {
      const cacheAgeMs = Date.now() - cached.data.meta.updated.seconds * 1000
      if (cacheAgeMs < CACHE_TTL_MS) {
        return cached.data.muxData
      }
    }
  }

  const idToken: string = await auth.currentUser.getIdToken(true)
  const params = forceRefresh ? "?forceRefresh=true" : ""
  const asset = await client
    .get(`/snap/api/v0/mux_assets/${playbackId}${params}`, {
      headers: { AuthToken: idToken }
    })
    .then(response => response.data)
  return asset
}

export const updateMuxTrack = async (
  playbackId: string,
  data: any,
  trackId: string
) => {
  const idToken: string = await auth.currentUser.getIdToken(true)
  const track = await client
    .put(
      `/snap/api/v0/mux_assets/${playbackId}/subtitles/${trackId ?? ""}`,
      { ...data },
      { headers: { AuthToken: idToken } }
    )
    .then(response => response.data)

  return track
}

export const createMuxTrack = async (playbackId: string, data: any) => {
  const idToken: string = await auth.currentUser.getIdToken(true)
  const track = await client
    .post(
      `/snap/api/v0/mux_assets/${playbackId}/tracks`,
      { ...data },
      { headers: { AuthToken: idToken } }
    )
    .then(response => response.data)
  return track
}

export const deleteMuxTrack = async (playbackId: string, trackId: string) => {
  const idToken: string = await auth.currentUser.getIdToken(true)
  await client.delete(
    `/snap/api/v0/mux_assets/${playbackId}/tracks/${trackId ?? ""}`,
    { headers: { AuthToken: idToken } }
  )
}

export const createMuxClip = async (
  playbackId: string,
  startTime: number | undefined,
  endTime: number | undefined,
  name: string,
  snapId: string
): Promise<{ asset_id: string; playback_id: string; status: string; library: any }> => {
  const idToken: string = await auth.currentUser.getIdToken(true)
  const response = await client.post(
    `/snap/api/v0/mux_assets/${playbackId}/clip`,
    {
      start_time: startTime,
      end_time: endTime,
      name,
      snap_id: snapId
    },
    { headers: { AuthToken: idToken } }
  )
  return response.data
}

export const generateCaptions = async (playbackId: string) => {
  const idToken: string = await auth.currentUser.getIdToken(true)
  const response = await client.post(
    `/snap/api/v0/mux_assets/${playbackId}/generate-captions`,
    {},
    { headers: { AuthToken: idToken } }
  )
  return response.data
}

export async function convertMedia(
  type: "video" | "ppt",
  fileName: string,
  snapId: string
): Promise<firebase.FirebaseError> {
  let result = {} as firebase.FirebaseError

  //convert PPT
  const config: AxiosRequestConfig = {
    baseURL: `${getEnv("AUTHORING_API")}`,
    responseType: "json"
  }

  const client = axios.create(config)

  const currentUser = firebase.auth().currentUser

  if (currentUser) {
    const idToken: string = (await currentUser
      .getIdToken(true)
      .catch((err: firebase.FirebaseError) => (result = err))) as string

    if (_.isEmpty(result)) {
      await client({
        method: "get",
        url: `/snap/api/v0/convert/${type}s/${snapId}/${encodeURIComponent(
          fileName
        )}`,
        headers: {
          AuthToken: idToken
        }
      })
        .then(res => {
          //@ts-ignore
          result = {
            code: "api/success",
            message: res.data.message,
            media: res.data.media
          } as firebase.FirebaseError
        })
        .catch(error => {
          result = {
            code: "api/error",
            message: error
          } as firebase.FirebaseError
        })
    }
  }
  return result
}

export async function uploadVideo(
  videoName: string,
  videoBlob: Blob,
  snapId: string
) {
  return new Promise((resolve, reject) => {
    if (!videoBlob) {
      reject({ code: "video/missing", message: "Video missing." })

      return
    }

    // 75mb
    if (videoBlob.size > 75 * 1024 * 1024) {
      console.log("error size")
      reject({
        code: "video/size",
        message: "Video size is greater than 75mb."
      })

      return
    }

    const vidReference = storage
      .ref()
      .child("videos")
      .child(snapId)
      .child(videoName)

    vidReference
      .put(videoBlob)
      .then(uploadTaskSnapshot => {
        vidReference
          .getDownloadURL()
          .then(value => {
          analytics.logEvent("upload_video")

          trackEvent({
            type: EventAction.UPLOAD_VIDEO,
          }, "raw")

          convertMedia("video", videoName, snapId).then(reason => {
            resolve(reason)
          })
        })
          .catch(reason => {
            reject(reason)
          })
      })
      .catch(reason => {
        reject(reason)
      })
  })
}

export async function uploadPdf(
  pdfName: string,
  pdfBlob: Blob,
  snapId: string,
  uid: string
) {
  return new Promise(async (resolve, reject) => {
    // 10 minutes
    const cacheTime = 10 * 60

    const meta = createMeta(uid)
    const asset = await createAssetRecord("pdf", pdfName, meta)

    const assetRef: AssetRef = {
      type: "pdf",
      id: asset.ref.id,
      snap_id: snapId
    }

    if (!pdfBlob) {
      reject({ code: "pdf/missing", message: "Pdf missing." })

      return
    }

    // 15mb
    if (pdfBlob.size > 15 * 1024 * 1024) {
      console.log("error size")
      reject({ code: "pdf/size", message: "Pdf size is greater than 15mb." })

      return
    }

    const pdfReference = storage
      .ref()
      .child("pdfs")
      .child(snapId)
      .child(asset.ref.id)

    pdfReference
      .put(pdfBlob)
      .then(uploadTaskSnapshot => {
        pdfReference
          .getDownloadURL()
          .then(value => {
          analytics.logEvent("upload_pdf")

          trackEvent({
            type: EventAction.UPLOAD_PDF,
          }, "raw")

          createLibraryRecord(
            "pdf",
            pdfName,
            snapId,
            getAssetLocation(assetRef),
            [assetRef],
            meta
          )

          resolve({ code: "pdf/success", message: "success" })
        })
          .catch(reason => {
            reject(reason)
          })
      })
      .catch(reason => {
        reject(reason)
      })
  })
}

export async function updateImage(id: string, name: string, altText: string) {
  return new Promise(async (resolve, reject) => {
    const libraryRecord = await getLibraryRecord(id)
    const interactionIds: string[] = []
    const interactionTypeMap = {
      // MISC Blocks
      image: "imageType",
      opentext: "richTextType",
      passage: "richTextType",
      // QTI Interactions
      choice: "simpleChoiceQtiType",
      order: "simpleChoiceQtiType",
      hotspot: "objectQtiType",
      gapmatch: "inlineChoiceQtiType",
      inlineChoice: "inlineChoiceQtiType",
      hottext: "inlineChoiceQtiType",
      textEntry: "inlineChoiceQtiType",
      match: "matchQtiType",
      extendedtext: "extendedTextQtiType"
    }

    function updateAssetInPromptOps(
      interactionRecord,
      updatedRecord,
      assetRecordId,
      altText
    ) {
      let assetFound = false

      // Check both possible paths for prompt.ops
      const promptPaths = [
        interactionRecord.data.content?.prompt?.ops,
        interactionRecord.data.content?.content?.prompt?.ops
      ]

      promptPaths.forEach((ops, index) => {
        if (ops) {
          const updatedOps = ops.map(op => {
            if (op.attributes && op.attributes.id === assetRecordId) {
              assetFound = true
              return {
                ...op,
                attributes: {
                  ...op.attributes,
                  alt: altText
                }
              }
            }
            return op
          })

          // Update the appropriate path in updatedRecord
          if (index === 0) {
            // content.prompt path
            if (!updatedRecord.content.prompt) updatedRecord.content.prompt = {}
            updatedRecord.content.prompt = {
              ...interactionRecord.data.content.prompt,
              ops: updatedOps
            }
          } else {
            // content.content.prompt path
            if (!updatedRecord.content.content)
              updatedRecord.content.content = {}
            if (!updatedRecord.content.content.prompt)
              updatedRecord.content.content.prompt = {}
            updatedRecord.content.content.prompt = {
              ...interactionRecord.data.content.content.prompt,
              ops: updatedOps
            }
          }
        }
      })

      return assetFound
    }

    if (!libraryRecord) {
      console.error("The library record does not exist")
      return
    }

    const assetRecordId = libraryRecord.data.assets[0]?.id
    const snapId = libraryRecord.data.assets[0]?.snap_id

    const updatedAsset = {
      ...libraryRecord.data?.assets[0],
      altText
    }

    await updateLibraryRecord(id, { name, assets: [updatedAsset] })
      .then(() => {
        resolve({ code: "image/update", message: "success" })
      })
      .catch(reason => reject(reason))

    await updateAssetRecord(assetRecordId, { name, altText })

    const snapRecord = await getSnapRecord(snapId)

    const screenIds =
      snapRecord.data.branches?.Main?.filter(
        screen => screen?.type === "screen"
      )?.map(screen => screen?.id) || []

    for (const screenId of screenIds) {
      const screenRecord = await getScreenRecord(screenId)

      const allItems = [
        ...(Array.isArray(screenRecord.data.left)
          ? screenRecord.data?.left
          : []),
        ...(Array.isArray(screenRecord.data.right)
          ? screenRecord.data?.right
          : [])
      ]

      const screenIds = allItems
        ?.filter(
          item =>
            (item?.type === "qtiInteraction" ||
              item?.type === "image" ||
              item?.type === "text" ||
              item?.type === "passage") &&
            item?.id
        )
        ?.map(item => item?.id)

      interactionIds.push(...screenIds)
    }

    for (const interactionId of interactionIds) {
      const interactionRecord =
        (await getQtiInteractionRecord(interactionId)) ||
        (await getBlockImageRecord(interactionId)) ||
        (await getBlockTextRecord(interactionId))
      const choiceType = interactionRecord?.data?.type
      const category = interactionTypeMap[choiceType]
      const updatedRecord = { ...interactionRecord?.data }
      let assetFound = false

      if (category === "simpleChoiceQtiType") {
        updatedRecord.content.simpleChoice =
          interactionRecord.data.content?.simpleChoice?.map(choice => {
            if (choice.asset && choice.asset?.id === assetRecordId) {
              assetFound = true
              return {
                ...choice,
                asset: {
                  ...choice?.asset,
                  altText: altText,
                  ...(choice?.asset?.fileName !== undefined && {
                    fileName: name
                  })
                }
              }
            }
            return choice
          })

        const promptAssetFound = updateAssetInPromptOps(
          interactionRecord,
          updatedRecord,
          assetRecordId,
          altText
        )
        assetFound = assetFound || promptAssetFound

        if (assetFound) {
          await updateQtiInteractionRecord(interactionId, updatedRecord)
        }
      } else if (category === "objectQtiType") {
        if (
          interactionRecord.data.content?.object?.attributes?.id ===
          assetRecordId
        ) {
          assetFound = true
          updatedRecord.content.object = {
            ...interactionRecord.data.content.object,
            attributes: {
              ...interactionRecord.data.content.object.attributes,
              alt: altText,
              fileName: name
            }
          }
        }
        const promptAssetFound = updateAssetInPromptOps(
          interactionRecord,
          updatedRecord,
          assetRecordId,
          altText
        )
        assetFound = assetFound || promptAssetFound
        if (assetFound) {
          await updateQtiInteractionRecord(interactionId, updatedRecord)
        }
      } else if (category === "imageType") {
        if (
          interactionRecord?.data?.assets &&
          interactionRecord?.data?.assets[0]?.id === assetRecordId
        ) {
          assetFound = true
          updatedRecord.assets = [
            {
              ...interactionRecord.data.assets[0],
              altText: altText
            }
          ]
        }
        if (assetFound) {
          await updateBlockImageRecord(interactionId, updatedRecord)
        }
      } else if (category === "richTextType") {
        if (
          interactionRecord?.data?.deltas?.ops &&
          Array.isArray(interactionRecord.data.deltas.ops)
        ) {
          const ops = interactionRecord.data.deltas.ops
          const updatedOps = ops.map(op => {
            if (op.attributes?.id === assetRecordId) {
              assetFound = true

              return {
                ...op,
                attributes: {
                  ...op.attributes,
                  alt: altText
                }
              }
            }
            return op
          })
          if (assetFound) {
            updatedRecord.deltas.ops = updatedOps
            if (
              interactionRecord.data.html &&
              typeof interactionRecord.data.html === "string"
            ) {
              updatedRecord.html = rewriteImageAlt(
                updatedRecord.html,
                assetRecordId,
                altText
              )
            }
          }
          await updateBlockTextRecord(interactionId, updatedRecord)
        }
      } else if (category === "inlineChoiceQtiType") {
        if (
          interactionRecord?.data?.content?.content?.deltas?.ops &&
          Array.isArray(interactionRecord.data.content.content.deltas.ops)
        ) {
          const ops = interactionRecord.data.content.content.deltas.ops
          const updatedOps = ops.map(op => {
            if (op.attributes?.id === assetRecordId) {
              assetFound = true
              return {
                ...op,
                attributes: {
                  ...op.attributes,
                  alt: altText
                }
              }
            }
            return op
          })

          const promptAssetFound = updateAssetInPromptOps(
            interactionRecord,
            updatedRecord,
            assetRecordId,
            altText
          )
          assetFound = assetFound || promptAssetFound

          if (assetFound) {
            updatedRecord.content.content.deltas.ops = updatedOps
            await updateQtiInteractionRecord(interactionId, updatedRecord)
          }
        }
      } else if (
        category === "matchQtiType" ||
        category === "extendedTextQtiType"
      ) {
        if (
          interactionRecord?.data?.content ||
          interactionRecord?.data?.content?.content?.ops
        ) {
          const promptAssetFound = updateAssetInPromptOps(
            interactionRecord,
            updatedRecord,
            assetRecordId,
            altText
          )
          if (promptAssetFound) {
            await updateQtiInteractionRecord(interactionId, updatedRecord)
          }
        }
      }
    }
  })
}

export async function uploadImage(
  imageName: string,
  imageBlob: Blob,
  snapId: string,
  uid: string,
  altText: string
) {
  return new Promise(async (resolve, reject) => {
    // 10 minutes
    const cacheTime = 10 * 60

    const meta = createMeta(uid)
    const asset = await createAssetRecord("image", imageName, meta, altText)

    const assetRef: AssetRef = {
      type: "image",
      id: asset.ref.id,
      snap_id: snapId,
      altText: altText
    }

    if (!imageBlob) {
      reject({ code: "image/missing", message: "Image missing." })

      return
    }

    // 5mb
    if (imageBlob.size > 5 * 1024 * 1024) {
      console.log("error size")
      reject({ code: "image/size", message: "Image size is greater than 5mb." })

      return
    }

    const imageReference = storage
      .ref()
      .child("images")
      .child(snapId)
      .child(asset.ref.id)

    imageReference
      .put(imageBlob)
      .then(uploadTaskSnapshot => {
        imageReference
          .getDownloadURL()
          .then(value => {
          analytics.logEvent("upload_image")

          trackEvent({
            type: EventAction.UPLOAD_IMAGE,
          }, "raw")

          createLibraryRecord(
            "image",
            imageName,
            snapId,
            getAssetLocation(assetRef),
            [assetRef],
            meta
          )

          resolve({ code: "upload/success", message: "success" })
        })
          .catch(reason => {
            reject(reason)
          })
      })
      .catch(reason => {
        reject(reason)
      })
  })
}

export async function uploadAndConvertPPT(
  uid: string,
  snapId: string,
  ppt: Blob,
  presentationName: string
): Promise<firebase.FirebaseError> {
  let result = {} as firebase.FirebaseError

  const pptReference = storage
    .ref()
    .child("ppts")
    .child(snapId)
    .child(presentationName)

  await pptReference
    .put(ppt)
    .then(async snapshot => {
      if (snapshot.state == firebase.storage.TaskState.SUCCESS) {
        result = await convertMedia("ppt", presentationName, snapId)
      }
    })
    .catch(err => (result = err as firebase.FirebaseError))
  return result
}

export async function uploadWordBank(
  name: string,
  textFile: Blob,
  snapId: string,
  uid: string
) {
  return new Promise(async (resolve, reject) => {
    // 10 minutes
    const cacheTime = 10 * 60

    const meta = createMeta(uid)
    const asset = await createAssetRecord("word-bank", name, meta)

    const bucketAssetRef: AssetRef = {
      type: "word-bank",
      id: asset.ref.id,
      snap_id: snapId
    }

    if (!textFile) {
      reject({ code: "text/missing", message: "Text file missing" })
      return
    }

    const fileReference = storage
      .ref()
      .child("word-banks")
      .child(snapId)
      .child(asset.ref.id)

    fileReference
      .put(textFile)
      .then(() => {
        const fileUrl = `https://firebasestorage.googleapis.com/v0/b/${getEnv(
          "BUCKET_URL"
        )}/o/${encodeURIComponent(getAssetLocation(bucketAssetRef))}?alt=media`
        axios.get(fileUrl).then(res => {
          addWordBank(
            WordBankSchema.cast({
              words: res.data
                .split("\n")
                .filter((word: string) => {
                  if (word.trim() != "") return true
                })
                .map((word: string) => {
                  return word.trim()
                })
            }) as WordBank
          ).then(doc => {
            const assetRef: AssetRef = {
              type: "word-bank",
              id: doc.ref.id,
              snap_id: snapId
            }
            createLibraryRecord(
              "word-bank",
              name,
              snapId,
              getAssetLocation(bucketAssetRef),
              [assetRef],
              meta
            )
            resolve({ code: "upload/success", message: "success" })
          })
        })
      })
      .catch(reason => {
        reject(reason)
      })
  })
}

export async function uploadContentResource(
  name: string,
  file: Blob,
  contentId: string,
  uid: string
): Promise<Doc<Media>> {
  const meta = createMeta(uid)
  const asset = await createAssetRecord("content-resource", name, meta)

  const assetRef: AssetRef = {
    type: "content-resource",
    id: asset.ref.id,
    snap_id: contentId
  }

  if (!file) {
    throw new Error("File missing")
  }

  const fileReference = storage
    .ref()
    .child("content-resources")
    .child(contentId)
    .child(name)

  await fileReference.put(file)

  const doc: Doc<Media> = await createLibraryRecord(
    "content-resource",
    name,
    contentId,
    getAssetLocation(assetRef),
    [assetRef],
    meta
  )

  return doc
}

export async function uploadVideoTrack(
  name: string,
  file: Blob,
  snapId: string
) {
  // Upload file to the storage.
  const fileRef = storage.ref(`/videos/${snapId}/${name}`)
  await fileRef.put(file)

  const fileUrl = await fileRef.getDownloadURL()
  return fileUrl
}

export const getAssetPath = (
  type: MediaType | AssetType,
  name: string,
  assetId: string,
  snapId: string
) => {
  switch (type) {
    case "mux":
    case "video":
      return {
        mainFolder: "videos",
        subFolder: snapId,
        assetName: name
      }
    case "image":
      return {
        mainFolder: "image",
        subFolder: snapId,
        assetName: assetId
      }
    case "ppt":
      return {
        mainFolder: "ppts",
        subFolder: snapId,
        assetName: name
      }
    case "pdf":
      return {
        mainFolder: "pdfs",
        subFolder: snapId,
        assetName: assetId
      }
    case "content-resource":
      return {
        mainFolder: "content-resources",
        subFolder: snapId,
        assetName: name
      }
    default:
      return {
        mainFolder: "",
        subFolder: "",
        assetName: ""
      }
  }
}

export async function getDownloadLink(
  type: MediaType | AssetType,
  name: string,
  assetId: string,
  snapId: string
) {
  const { mainFolder, subFolder, assetName } = getAssetPath(
    type,
    name,
    assetId,
    snapId
  )

  if (mainFolder) {
    const fileRef = storage
      .ref()
      .child(mainFolder)
      .child(subFolder)
      .child(assetName)

    const meta = await fileRef.getMetadata()
    const url = await fileRef.getDownloadURL()

    return { url: url, meta: meta }
  } else {
    return { url: "", meta: null }
  }
}

export async function deleteMedia(mediaId: string) {
  const library = collection<Media>("library")
  const assets = collection<Asset>("assets")

  const mediaDoc = await get(library, mediaId)

  if (!mediaDoc) {
    return
  }

  const muxAssets = mediaDoc.data.assets.filter(asset => asset.type === "mux")

  // remove references to the video in all blocks within the snap
  if (mediaDoc.data.type === "video" && muxAssets.length > 0) {
    for (const asset of muxAssets) {
      try {
        await deleteVideoReferencesInSnap(asset.id, mediaDoc.data.snap_id)
      } catch (error) {
        console.error("Failed to delete video references:", error)
      }
    }
  }

  // Soft delete the library record
  await updateLibraryRecord(mediaId, {
    meta: {
      ...mediaDoc.data.meta,
      deleted: true
    }
  })
}


// Helper to check if assets array contains a specific playback ID
function hasVideoPlaybackId(assets: AssetRef[], playbackId: string): boolean {
  if (!assets || !Array.isArray(assets)) return false
  return assets.some(asset => asset.type === "mux" && asset.id === playbackId)
}

// Helper to remove video assets from an array (returns null if no video found)
function removeVideoAssetsFromArray(
  assets: AssetRef[],
  playbackId: string
): AssetRef[] | null {
  if (!assets || !Array.isArray(assets)) return null

  const hasVideo = assets.some(
    asset => asset.type === "mux" && asset.id === playbackId
  )

  if (!hasVideo) return null

  // Remove mux asset and its tracks
  return assets.filter(
    asset =>
      !(asset.type === "mux" && asset.id === playbackId) &&
      asset.type !== "mux_subtitles" &&
      asset.type !== "mux_audio"
  )
}

/**
 * CHRIS HERE: this shouldn't really be necessary. The system should handle non-existent /
 * deleted library entries. If it doesn't then that is the bug, and not the other way
 * around as deleted refs are expected to exist.
 *
 * With that said we have an issue were Mux ids are being directly inputed into
 * content records instead of their associated library record id--this abstracts
 * the video reference and allows us to delete the library record without leaving dangling
 * references in content records. This function is a bandaid to remove those dangling refs
 * when a video is deleted from the library. We need to fix the content so that
 * mux ids are not directly referenced and then we can remove this function and the
 * associated logic in deleteMedia.
 *
 * Delete all video references in a snap when a video is deleted from library.
 * This:
 * 1) Removes the library record (handled by deleteMedia caller)
 * 2) Sets screen references back to empty: { id: "", type: "empty" }
 * 3) Deletes the block_videos documents
 * 4) Removes video references from QTI and legacy interaction feedbacks
 */
export async function deleteVideoReferencesInSnap(
  playbackId: string,
  snapId: string
): Promise<{
  screenRefsReset: number
  blockVideosDeleted: number
  qtiInteractionsUpdated: number
  interactionsUpdated: number
}> {
  const result = {
    screenRefsReset: 0,
    blockVideosDeleted: 0,
    qtiInteractionsUpdated: 0,
    interactionsUpdated: 0
  }

  try {
    // Get snap record to find all screens
    const snapRecord = await getSnapRecord(snapId)
    if (!snapRecord) {
      console.error("Snap record not found:", snapId)
      return result
    }

    // Get all screen IDs from the snap's branches
    const screenIds =
      snapRecord.data.branches?.Main?.filter(
        screen => screen?.type === "screen"
      )?.map(screen => screen?.id) || []

    // Track blocks to delete and their screen associations
    const videoBlocksToDelete: Array<{ blockId: string; screenId: string }> = []
    const qtiBlockIds: string[] = []
    const interactionBlockIds: string[] = []

    // First pass: collect all block IDs from screens
    for (const screenId of screenIds) {
      const screenRecord = await getScreenRecord(screenId)
      if (!screenRecord) continue

      const allItems = [
        ...(Array.isArray(screenRecord.data.left)
          ? screenRecord.data.left
          : []),
        ...(Array.isArray(screenRecord.data.right)
          ? screenRecord.data.right
          : [])
      ]

      for (const item of allItems) {
        if (!item?.id) continue
        if (item.type === "video") {
          videoBlocksToDelete.push({ blockId: item.id, screenId })
        } else if (item.type === "qtiInteraction") {
          qtiBlockIds.push(item.id)
        } else if (item.type === "interaction") {
          interactionBlockIds.push(item.id)
        }
      }
    }

    // Step 1: Find which video blocks contain this playback ID and reset screen refs
    const blocksToDeleteByScreen: Map<string, string[]> = new Map()

    for (const { blockId, screenId } of videoBlocksToDelete) {
      const blockRecord = await getBlockVideoRecord(blockId)
      if (!blockRecord) continue

      if (hasVideoPlaybackId(blockRecord.data.assets, playbackId)) {
        const existingBlocks = blocksToDeleteByScreen.get(screenId) || []
        existingBlocks.push(blockId)
        blocksToDeleteByScreen.set(screenId, existingBlocks)
      }
    }

    // Step 2: Update screens - replace video block refs with empty refs
    for (const [screenId, blockIds] of blocksToDeleteByScreen) {
      const screenRecord = await getScreenRecord(screenId)
      if (!screenRecord) continue

      const updatedLeft = Array.isArray(screenRecord.data.left)
        ? screenRecord.data.left.map((item: any) =>
            blockIds.includes(item?.id) ? { id: "", type: "empty" } : item
          )
        : []
      const updatedRight = Array.isArray(screenRecord.data.right)
        ? screenRecord.data.right.map((item: any) =>
            blockIds.includes(item?.id) ? { id: "", type: "empty" } : item
          )
        : []

      await updateScreenRecord(screenId, {
        left: updatedLeft,
        right: updatedRight
      })
      result.screenRefsReset += blockIds.length
      console.log(
        `Reset ${blockIds.length} video block refs to empty on screen ${screenId}`
      )
    }

    // Step 3: Soft delete block_videos documents (set meta.deleted = true and clear assets)
    for (const [, blockIds] of blocksToDeleteByScreen) {
      for (const blockId of blockIds) {
        try {
          const blockRecord = await getBlockVideoRecord(blockId)
          if (blockRecord) {
            await updateBlockVideoRecord(blockId, {
              meta: {
                ...blockRecord.data.meta,
                deleted: true
              }
            })
            result.blockVideosDeleted++
            console.log(`Soft deleted block_video ${blockId}`)
          }
        } catch (error) {
          console.error(`Failed to soft delete block_video ${blockId}:`, error)
        }
      }
    }

    // Step 4: Remove video references from QTI interaction feedbacks
    for (const blockId of qtiBlockIds) {
      const blockRecord = await getQtiInteractionRecord(blockId)
      if (!blockRecord) continue

      let hasChanges = false
      const updatedData: any = {}

      // Update answerFeedbacks - remove video feedbacks with this playback ID
      if (
        blockRecord.data.answerFeedbacks &&
        Array.isArray(blockRecord.data.answerFeedbacks)
      ) {
        const updatedFeedbacks = blockRecord.data.answerFeedbacks.map(
          (feedback: any) => {
            if (feedback.type !== "video") return feedback
            const updatedAssets = removeVideoAssetsFromArray(
              feedback.assets,
              playbackId
            )
            if (updatedAssets !== null) {
              hasChanges = true
              // If no assets left, clear the feedback
              if (updatedAssets.length === 0) {
                return { ...feedback, assets: [], type: "none" }
              }
              return { ...feedback, assets: updatedAssets }
            }
            return feedback
          }
        )
        if (hasChanges) {
          updatedData.answerFeedbacks = updatedFeedbacks
        }
      }

      // Update generalFeedbacks
      if (
        blockRecord.data.generalFeedbacks &&
        Array.isArray(blockRecord.data.generalFeedbacks)
      ) {
        const prevHasChanges = hasChanges
        const updatedGeneralFeedbacks = blockRecord.data.generalFeedbacks.map(
          (feedback: any) => {
            if (feedback.type !== "video") return feedback
            const updatedAssets = removeVideoAssetsFromArray(
              feedback.assets,
              playbackId
            )
            if (updatedAssets !== null) {
              hasChanges = true
              if (updatedAssets.length === 0) {
                return { ...feedback, assets: [], type: "none" }
              }
              return { ...feedback, assets: updatedAssets }
            }
            return feedback
          }
        )
        if (hasChanges && !prevHasChanges) {
          updatedData.generalFeedbacks = updatedGeneralFeedbacks
        } else if (hasChanges) {
          updatedData.generalFeedbacks = updatedGeneralFeedbacks
        }
      }

      if (hasChanges) {
        await updateQtiInteractionRecord(blockId, updatedData)
        result.qtiInteractionsUpdated++
        console.log(`Removed video from qti_interaction ${blockId}`)
      }
    }

    // Step 5: Remove video references from legacy interaction feedbacks
    for (const blockId of interactionBlockIds) {
      const blockRecord = await getInteractionRecord(blockId)
      if (!blockRecord) continue

      let hasChanges = false
      const updatedQuestions = blockRecord.data.questions?.map(
        (question: any) => {
          let questionChanged = false
          const updatedQuestion = { ...question }

          // Update question.feedbacks
          if (question.feedbacks && Array.isArray(question.feedbacks)) {
            const updatedFeedbacks = question.feedbacks.map((feedback: any) => {
              if (feedback.type !== "video") return feedback
              const updatedAssets = removeVideoAssetsFromArray(
                feedback.assets,
                playbackId
              )
              if (updatedAssets !== null) {
                hasChanges = true
                questionChanged = true
                if (updatedAssets.length === 0) {
                  return { ...feedback, assets: [], type: "none" }
                }
                return { ...feedback, assets: updatedAssets }
              }
              return feedback
            })
            if (questionChanged) {
              updatedQuestion.feedbacks = updatedFeedbacks
            }
          }

          // Update question.answers[].feedbacks
          if (question.answers && Array.isArray(question.answers)) {
            const updatedAnswers = question.answers.map((answer: any) => {
              if (!answer.feedbacks || !Array.isArray(answer.feedbacks)) {
                return answer
              }
              let answerChanged = false
              const updatedAnswerFeedbacks = answer.feedbacks.map(
                (feedback: any) => {
                  if (feedback.type !== "video") return feedback
                  const updatedAssets = removeVideoAssetsFromArray(
                    feedback.assets,
                    playbackId
                  )
                  if (updatedAssets !== null) {
                    hasChanges = true
                    answerChanged = true
                    if (updatedAssets.length === 0) {
                      return { ...feedback, assets: [], type: "none" }
                    }
                    return { ...feedback, assets: updatedAssets }
                  }
                  return feedback
                }
              )
              if (answerChanged) {
                return { ...answer, feedbacks: updatedAnswerFeedbacks }
              }
              return answer
            })
            if (hasChanges) {
              updatedQuestion.answers = updatedAnswers
            }
          }

          return updatedQuestion
        }
      )

      if (hasChanges) {
        await updateInteractionRecord(blockId, { questions: updatedQuestions })
        result.interactionsUpdated++
        console.log(`Removed video from interaction ${blockId}`)
      }
    }
  } catch (error) {
    console.error("Failed to delete video references:", error)
    throw error
  }

  return result
}
